/**
 * @fileoverview Composition root — the only place concrete adapters are wired.
 *
 * Everything above this file depends on ports; everything below implements
 * them. This is also the single point where `import.meta.env` is read, which is
 * what makes the "am I pointed at a live environment?" question answerable in
 * one place instead of being rediscovered inside each module.
 *
 * The container is a lazily-built singleton rather than eagerly constructed at
 * import time: module-level construction would make a config error surface as
 * an opaque import failure during bundle evaluation, before any error boundary
 * exists to report it.
 */

import { parseAppConfig, type AppConfig, type ConfigSource } from "./domain/config.js";
import type { AppCatalogPort } from "./ports/app-catalog.js";
import type { AuthPort } from "./ports/auth.js";
import type { HttpClientPort, TokenProvider } from "./ports/http.js";
import { CognitoAuthAdapter } from "./adapters/cognito-auth.js";
import { FetchHttpClient } from "./adapters/fetch-http.js";
import { StaticAppCatalogAdapter } from "./adapters/static-app-catalog.js";

export interface Container {
  readonly config: AppConfig;
  readonly auth: AuthPort;
  readonly http: HttpClientPort;
  /**
   * Which applications exist. Static today; the point of the port is that
   * swapping it for an API-backed or per-agency-gated catalog is a change in
   * this file and nowhere else.
   */
  readonly appCatalog: AppCatalogPort;
  /**
   * Install the bearer-token source. Called by the composition root once the
   * Cognito session resolves; until then requests go out unauthenticated,
   * which is correct for the pre-sign-in surface.
   */
  setTokenProvider(provider: TokenProvider): void;
}

export function createContainer(source: ConfigSource): Container {
  const config = parseAppConfig(source);

  // Indirection so the provider can be replaced after construction without
  // rebuilding the client — tokens refresh in the background.
  let provider: TokenProvider = () => null;

  const auth = new CognitoAuthAdapter(config);
  const http = new FetchHttpClient(config.apiBaseUrl, () => provider());
  const appCatalog = new StaticAppCatalogAdapter();

  return {
    config,
    auth,
    http,
    appCatalog,
    setTokenProvider(next: TokenProvider) {
      provider = next;
    },
  };
}

let instance: Container | null = null;

/** The app-wide container, built on first use from `import.meta.env`. */
export function container(): Container {
  instance ??= createContainer(import.meta.env as unknown as ConfigSource);
  return instance;
}

/** Test seam: install a container built from explicit config. */
export function setContainer(next: Container | null): void {
  instance = next;
}
